/**
 * AdminMapeoBodegas · /admin/mapeo-bodegas
 * v3.9.9 — fix React #31 (usa `extractApiError` para no pasar objetos a toast)
 *         + Mapeo de Zonas por Bodega (many-to-many via warehouse_zone_mapping).
 *
 * Cada bodega puede asignarse a una Sucursal (1:1) y a N Zonas de Cobertura.
 * Los agentes A1/A2 usan `GET /v1/mesa/warehouse-mapping/by-zone/{zone_id}`
 * para saber qué bodegas surten cada zona geográfica.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import FloatingPanel from "../components/ui/FloatingPanel";   // v101.98.0 · fuera de la tabla
import Layout from "../components/Layout";
import api, { extractApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Warehouse, RefreshCw, Wand2, Filter, Loader2, MapPin, Plus, X, CheckSquare, Ban,
} from "lucide-react";

export default function AdminMapeoBodegas() {
  const [items, setItems] = useState([]);
  const [zones, setAllZones] = useState([]);
  const [branchOptions, setBranchOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [onlyUnmapped, setOnlyUnmapped] = useState(false);
  const [saving, setSaving] = useState(null);
  const [autoRunning, setAutoRunning] = useState(false);
  const [q, setQ] = useState("");
  const [zonePickerOpen, setZonePickerOpen] = useState(null); // location_id | null
  // v100.3.0 · Filtros + selección masiva + NODO
  const [branchFilter, setBranchFilter] = useState("");   // "" | "__none__" | nomenclatura
  const [zoneFilter, setZoneFilter] = useState("");       // "" | "__none__" | zone_id
  const [showNodes, setShowNodes] = useState(false);
  const [sel, setSel] = useState(() => new Set());
  const [bulkBranch, setBulkBranch] = useState("");
  const [bulkZones, setBulkZones] = useState([]);
  const [bulkZoneOpen, setBulkZoneOpen] = useState(false);
  const bulkZoneAnchorRef = useRef(null);   // v101.98.0
  const [bulkRunning, setBulkRunning] = useState(false);

  // v10 · Carga dinámica de sucursales desde PG (tabla branches)
  const loadBranches = async () => {
    try {
      const { data } = await api.get("/v1/config/branches");
      const list = Array.isArray(data) ? data : (data?.items || []);
      setBranchOptions(list.filter((b) => b.active !== false).map((b) => ({
        nom: b.nomenclatura || b.code,
        name: b.name,
        id: b.id,
      })));
    } catch {
      setBranchOptions([]);
    }
  };

  const loadZones = async () => {
    try {
      const { data } = await api.get("/admin/zonas");
      const arr = Array.isArray(data) ? data : (data?.items || []);
      setAllZones(arr);
    } catch (e) {
      toast.error(extractApiError(e, "Error cargando zonas"));
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/v1/mesa/warehouse-mapping", {
        params: { unmapped_only: onlyUnmapped },
      });
      setItems(data.items || []);
    } catch (e) {
      toast.error(extractApiError(e, "Error cargando mapeo"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadZones(); loadBranches(); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [onlyUnmapped]);

  const setMapping = async (location_id, branch_nomenclatura) => {
    setSaving(location_id);
    try {
      await api.put("/v1/mesa/warehouse-mapping", {
        location_id,
        branch_nomenclatura: branch_nomenclatura || null,
      });
      toast.success(`Bodega ${location_id} → ${branch_nomenclatura || "—"}`);
      await load();
    } catch (e) {
      toast.error(extractApiError(e, "Error guardando"));
    } finally {
      setSaving(null);
    }
  };

  const saveZones = async (location_id, zone_ids) => {
    setSaving(location_id);
    try {
      await api.put("/v1/mesa/warehouse-mapping/zones", { location_id, zone_ids });
      toast.success(`Bodega ${location_id}: ${zone_ids.length} zona(s) asignada(s)`);
      await load();
    } catch (e) {
      toast.error(extractApiError(e, "Error guardando zonas"));
    } finally {
      setSaving(null);
      setZonePickerOpen(null);
    }
  };

  const autoMap = async () => {
    setAutoRunning(true);
    try {
      const { data } = await api.post("/v1/mesa/warehouse-mapping/auto-map");
      toast.success(`Auto-mapeadas ${data.auto_mapped} de ${data.total_candidates} bodegas`);
      await load();
    } catch (e) {
      toast.error(extractApiError(e, "Error auto-mapping"));
    } finally {
      setAutoRunning(false);
    }
  };

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return items.filter((i) => {
      if (!showNodes && i.is_node) return false;
      if (ql && !JSON.stringify(i).toLowerCase().includes(ql)) return false;
      if (branchFilter === "__none__" && i.branch_id) return false;
      if (branchFilter && branchFilter !== "__none__" && i.branch_nomenclatura !== branchFilter) return false;
      const zs = i.zones || [];
      if (zoneFilter === "__none__" && zs.length) return false;
      if (zoneFilter && zoneFilter !== "__none__" && !zs.some((z) => z.id === zoneFilter)) return false;
      return true;
    });
  }, [items, q, branchFilter, zoneFilter, showNodes]);

  const selectable = filtered.filter((i) => !i.is_node);
  const allSelected = selectable.length > 0 && selectable.every((i) => sel.has(i.location_id));
  const toggleAll = () => setSel((prev) => {
    const n = new Set(prev);
    if (allSelected) selectable.forEach((i) => n.delete(i.location_id));
    else selectable.forEach((i) => n.add(i.location_id));
    return n;
  });
  const toggleOne = (id) => setSel((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const nodeCount = items.filter((i) => i.is_node).length;
  const nodeMapped = items.filter((i) => i.is_node && (i.branch_id || (i.zones || []).length)).length;

  const runBulk = async (action) => {
    const ids = [...sel];
    if (!ids.length) return;
    if (action === "set_branch" && !bulkBranch) { toast.error("Elija la sucursal"); return; }
    if (["add_zones", "remove_zones", "set_zones"].includes(action) && !bulkZones.length && action !== "set_zones") { toast.error("Elija al menos una zona"); return; }
    const labels = {
      set_branch: `asignar la sucursal ${bulkBranch}`, clear_branch: "quitar la sucursal",
      add_zones: `agregar ${bulkZones.length} zona(s)`, remove_zones: `quitar ${bulkZones.length} zona(s)`,
      set_zones: `reemplazar las zonas por ${bulkZones.length} zona(s)`, clear_zones: "quitar todas las zonas",
      clear_all: "quitar sucursal y zonas",
    };
    if (!window.confirm(`¿${labels[action]} a ${ids.length} bodega(s)?`)) return;
    setBulkRunning(true);
    try {
      const { data } = await api.post("/v1/mesa/warehouse-mapping/bulk", {
        location_ids: ids, action, branch_nomenclatura: bulkBranch || null, zone_ids: bulkZones,
      });
      toast.success(`${data.updated} bodega(s) actualizadas${data.skipped_nodes?.length ? ` · ${data.skipped_nodes.length} NODO omitidas` : ""}`);
      setSel(new Set()); setBulkZoneOpen(false);
      await load();
    } catch (e) {
      toast.error(extractApiError(e, "Error en la acción masiva"));
    } finally { setBulkRunning(false); }
  };

  const clearNodes = async () => {
    if (!window.confirm(`¿Quitar sucursal y zonas a todas las bodegas NODO (${nodeMapped} con mapeo)?`)) return;
    try {
      const { data } = await api.post("/v1/mesa/warehouse-mapping/clear-nodes");
      toast.success(`NODO: ${data.branch_mappings_removed} sucursales y ${data.zone_mappings_removed} zonas quitadas`);
      await load();
    } catch (e) { toast.error(extractApiError(e, "Error limpiando NODO")); }
  };

  const real = items.filter((i) => !i.is_node);
  const mapped = real.filter((i) => i.branch_id).length;
  const pending = real.length - mapped;
  const withZones = real.filter((i) => (i.zones || []).length > 0).length;

  return (
    <Layout>
      <div className="space-y-4" data-testid="wh-mapping-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full mb-2" style={{ background: "#FEF3C7", border: "1px solid #FBBF24" }}>
              <Warehouse size={11} className="text-amber-700"/>
              <span className="text-[10px] font-semibold uppercase tracking-widest text-amber-800">Mapeo de Bodegas · Sucursal + Zonas</span>
            </div>
            <h1 className="text-3xl font-bold" style={{ color: "#0A3747" }}>Bodegas Odoo → Sucursales + Zonas</h1>
            <p className="mt-1 text-sm text-gray-500 max-w-3xl">
              Cada bodega Odoo debe asignarse a una <b>Sucursal</b> (1:1) y a una o más <b>Zonas de Cobertura</b> (many-to-many). A1/A2 usan este mapeo para saber qué bodegas pueden surtir materiales a cada zona geográfica.
            </p>
            <p className="mt-1 text-xs text-gray-500">
              Bodegas <b>{real.length}</b> · Sucursal ✅ <b className="text-emerald-700">{mapped}</b> / ❌ <b className="text-red-700">{pending}</b> · Con zonas: <b className="text-blue-700">{withZones}</b>
              {" · "}NODO (no se mapean): <b className="text-gray-700">{nodeCount}</b>
              {nodeMapped > 0 && <> · <b className="text-red-700">{nodeMapped} NODO con mapeo</b> <button onClick={clearNodes} className="underline text-red-700 ml-1" data-testid="btn-clear-nodes">quitar</button></>}
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={autoMap} disabled={autoRunning} className="neu-btn neu-btn-primary" data-testid="btn-auto-map">
              {autoRunning ? <Loader2 size={14} className="animate-spin"/> : <Wand2 size={14}/>} Auto-mapear por prefijo
            </button>
            <button onClick={load} className="neu-btn"><RefreshCw size={14}/> Refrescar</button>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, ID, sucursal o zona..." className="flex-1 min-w-[260px] px-3 py-2 border rounded text-sm" data-testid="search-input"/>
          <select value={branchFilter} onChange={(e) => setBranchFilter(e.target.value)} className="px-2 py-2 border rounded text-xs" data-testid="filter-branch">
            <option value="">Todas las sucursales</option>
            <option value="__none__">Sin sucursal</option>
            {branchOptions.map((b) => <option key={b.nom} value={b.nom}>{b.nom} · {b.name}</option>)}
          </select>
          <select value={zoneFilter} onChange={(e) => setZoneFilter(e.target.value)} className="px-2 py-2 border rounded text-xs max-w-[220px]" data-testid="filter-zone">
            <option value="">Todas las zonas</option>
            <option value="__none__">Sin zonas</option>
            {zones.map((z) => <option key={z.id} value={z.id}>{z.code ? `${z.code} · ` : ""}{z.name}</option>)}
          </select>
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={onlyUnmapped} onChange={(e) => setOnlyUnmapped(e.target.checked)} data-testid="toggle-unmapped"/>
            <Filter size={12}/> Solo pendientes de sucursal
          </label>
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={showNodes} onChange={(e) => setShowNodes(e.target.checked)} data-testid="toggle-nodes"/>
            Mostrar bodegas NODO
          </label>
        </div>

        {sel.size > 0 && (
          <div className="sticky top-0 z-30 flex items-center gap-2 flex-wrap p-3 rounded-xl border-2 shadow-lg bg-white" style={{ borderColor: "#0A3747" }} data-testid="bulk-bar">
            <span className="text-xs font-bold text-[#0A3747] flex items-center gap-1"><CheckSquare size={14}/> {sel.size} seleccionada(s)</span>
            <span className="w-px h-5 bg-gray-300" />
            <select value={bulkBranch} onChange={(e) => setBulkBranch(e.target.value)} className="px-2 py-1 border rounded text-xs" data-testid="bulk-branch">
              <option value="">Sucursal…</option>
              {branchOptions.map((b) => <option key={b.nom} value={b.nom}>{b.nom} · {b.name}</option>)}
            </select>
            <button onClick={() => runBulk("set_branch")} disabled={bulkRunning} className="neu-btn neu-btn-primary text-xs" data-testid="bulk-set-branch">Asignar sucursal</button>
            <button onClick={() => runBulk("clear_branch")} disabled={bulkRunning} className="neu-btn text-xs" data-testid="bulk-clear-branch">Quitar sucursal</button>
            <span className="w-px h-5 bg-gray-300" />
            <div className="relative" ref={bulkZoneAnchorRef}>
              <button onClick={() => setBulkZoneOpen((o) => !o)} className="neu-btn text-xs" data-testid="bulk-zones-pick">
                <MapPin size={12}/> Zonas ({bulkZones.length})
              </button>
              {bulkZoneOpen && (
                <FloatingPanel anchorRef={bulkZoneAnchorRef} maxHeight={256} className="w-[300px] bg-white border rounded-lg shadow-xl p-2">
                  {zones.map((z) => (
                    <label key={z.id} className="flex items-center gap-2 px-1 py-1 text-xs cursor-pointer hover:bg-blue-50">
                      <input type="checkbox" checked={bulkZones.includes(z.id)}
                        onChange={() => setBulkZones((p) => (p.includes(z.id) ? p.filter((x) => x !== z.id) : [...p, z.id]))} />
                      <span className="font-mono text-[10px] text-gray-500 w-12">{z.code || "—"}</span>{z.name}
                    </label>
                  ))}
                </FloatingPanel>
              )}
            </div>
            <button onClick={() => runBulk("add_zones")} disabled={bulkRunning} className="neu-btn text-xs" data-testid="bulk-add-zones">Agregar zonas</button>
            <button onClick={() => runBulk("set_zones")} disabled={bulkRunning} className="neu-btn text-xs" data-testid="bulk-set-zones">Reemplazar zonas</button>
            <button onClick={() => runBulk("remove_zones")} disabled={bulkRunning} className="neu-btn text-xs" data-testid="bulk-remove-zones">Quitar estas zonas</button>
            <button onClick={() => runBulk("clear_zones")} disabled={bulkRunning} className="neu-btn text-xs" data-testid="bulk-clear-zones">Quitar todas las zonas</button>
            <button onClick={() => runBulk("clear_all")} disabled={bulkRunning} className="neu-btn text-xs text-red-700" data-testid="bulk-clear-all"><Ban size={12}/> Quitar todo el mapeo</button>
            <button onClick={() => setSel(new Set())} className="ml-auto text-xs underline text-gray-500">Limpiar selección</button>
            {bulkRunning && <Loader2 size={14} className="animate-spin" />}
          </div>
        )}

        {loading ? <div className="text-center py-12"><Loader2 size={20} className="animate-spin"/></div> : (
          <div className="neu-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 w-8">
                      <input type="checkbox" checked={allSelected} onChange={toggleAll} title="Seleccionar todas las visibles" data-testid="select-all"/>
                    </th>
                    <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest font-bold text-gray-500">Location ID</th>
                    <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest font-bold text-gray-500">Bodega Odoo</th>
                    <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest font-bold text-gray-500">Productos</th>
                    <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest font-bold text-gray-500">Stock</th>
                    <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest font-bold text-gray-500">Sucursal</th>
                    <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest font-bold text-gray-500 min-w-[220px]">Zonas de cobertura</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((it) => (
                    <tr key={it.location_id} className={`border-t ${it.is_node ? "bg-gray-100 opacity-70" : !it.branch_id ? "bg-red-50/30" : ""} ${sel.has(it.location_id) ? "!bg-blue-50" : ""}`} data-testid={`row-${it.location_id}`}>
                      <td className="px-3 py-1.5">
                        {!it.is_node && <input type="checkbox" checked={sel.has(it.location_id)} onChange={() => toggleOne(it.location_id)} data-testid={`sel-${it.location_id}`}/>}
                      </td>
                      <td className="px-3 py-1.5 font-mono text-xs">{it.location_id}</td>
                      <td className="px-3 py-1.5">
                        <div className="font-semibold text-sm" style={{ color: "#0A3747" }}>{it.location_name}</div>
                        <div className="text-[10px] text-gray-500 font-mono">{it.location_complete_name}</div>
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{it.products}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums font-mono">{Number(it.total_onhand || 0).toLocaleString("es-EC")}</td>
                      <td className="px-3 py-1.5">
                        {it.is_node ? (
                          <span className="text-[10px] px-2 py-0.5 rounded bg-gray-200 text-gray-600 font-bold">NODO · no se mapea{it.branch_id ? ` (tiene ${it.branch_nomenclatura})` : ""}</span>
                        ) : (
                        <div className="flex items-center gap-2">
                          <select
                            value={it.branch_nomenclatura || ""}
                            onChange={(e) => setMapping(it.location_id, e.target.value)}
                            disabled={saving === it.location_id}
                            className={`px-2 py-1 border rounded text-xs font-mono ${it.branch_id ? "border-emerald-300 bg-emerald-50" : "border-red-300 bg-red-50"}`}
                            data-testid={`select-${it.location_id}`}
                          >
                            <option value="">— Sin mapear —</option>
                            {branchOptions.map((b) => (
                              <option key={b.nom} value={b.nom}>{b.nom} · {b.name}</option>
                            ))}
                          </select>
                          {it.auto_mapped && it.branch_id && <span className="text-[9px] px-1 rounded bg-blue-100 text-blue-700">AUTO</span>}
                          {saving === it.location_id && <Loader2 size={12} className="animate-spin"/>}
                        </div>
                        )}
                      </td>
                      <td className="px-3 py-1.5">
                        {it.is_node ? (
                          <span className="text-[10px] italic text-gray-400">{(it.zones || []).length ? `${it.zones.length} zona(s) — usar "quitar" arriba` : "—"}</span>
                        ) : (
                        <ZonesCell
                          location_id={it.location_id}
                          zones={it.zones || []}
                          allZones={zones}
                          open={zonePickerOpen === it.location_id}
                          onOpen={() => setZonePickerOpen(it.location_id)}
                          onClose={() => setZonePickerOpen(null)}
                          onSave={(zone_ids) => saveZones(it.location_id, zone_ids)}
                          saving={saving === it.location_id}
                        />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filtered.length === 0 && <div className="text-center py-8 text-gray-400 text-sm">Sin bodegas para mostrar</div>}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

/**
 * ZonesCell — chips inline + botón "+ Zona" que abre un picker overlay.
 * El picker permite marcar/desmarcar zonas y guardar el conjunto completo.
 */
function ZonesCell({ location_id, zones, allZones, open, onOpen, onClose, onSave, saving }) {
  const pickerAnchorRef = useRef(null);   // v101.98.0
  const [selected, setSelected] = useState(zones.map((z) => z.id));
  const [q, setQ] = useState("");

  useEffect(() => {
    if (open) setSelected(zones.map((z) => z.id));
  }, [open, zones]);

  const filtered = q.trim()
    ? allZones.filter((z) =>
        `${z.code || ""} ${z.name || ""}`.toLowerCase().includes(q.trim().toLowerCase())
      )
    : allZones;

  const toggle = (zid) => {
    setSelected((prev) => prev.includes(zid) ? prev.filter((x) => x !== zid) : [...prev, zid]);
  };

  const removeChip = async (zid) => {
    const next = zones.map((z) => z.id).filter((x) => x !== zid);
    await onSave(next);
  };

  if (!open) {
    return (
      <div className="flex items-center gap-1 flex-wrap" data-testid={`zones-cell-${location_id}`}>
        {zones.length === 0 ? (
          <span className="text-[10px] italic text-gray-400">— sin zonas —</span>
        ) : (
          zones.map((z) => (
            <span
              key={z.id}
              className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 border border-blue-200 font-mono"
              data-testid={`zone-chip-${location_id}-${z.id}`}
            >
              <MapPin size={9} />
              {z.code || z.name}
              <button
                onClick={() => removeChip(z.id)}
                disabled={saving}
                className="ml-0.5 hover:text-red-700"
                title="Quitar zona"
                data-testid={`zone-remove-${location_id}-${z.id}`}
              >
                <X size={9} />
              </button>
            </span>
          ))
        )}
        <button
          onClick={onOpen}
          disabled={saving}
          className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border border-dashed border-gray-300 text-gray-500 hover:text-emerald-700 hover:border-emerald-400"
          data-testid={`zones-add-${location_id}`}
        >
          <Plus size={10} /> Zona
        </button>
      </div>
    );
  }

  return (
    <div className="relative" ref={pickerAnchorRef}>
      <FloatingPanel
        anchorRef={pickerAnchorRef}
        maxHeight={520}
        className="w-[340px] bg-white border rounded-lg shadow-xl p-3"
        data-testid={`zones-picker-${location_id}`}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-bold text-[#0A3747]">Zonas para bodega #{location_id}</div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid={`zones-picker-close-${location_id}`}>
            <X size={14} />
          </button>
        </div>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Filtrar zonas..."
          className="w-full px-2 py-1 border rounded text-xs mb-2"
          autoFocus
          data-testid={`zones-picker-search-${location_id}`}
        />
        <div className="max-h-56 overflow-y-auto border rounded">
          {filtered.length === 0 && (
            <div className="text-[10px] text-gray-400 italic p-2">Sin zonas coincidentes.</div>
          )}
          {filtered.map((z) => {
            const checked = selected.includes(z.id);
            return (
              <label
                key={z.id}
                className={`flex items-center gap-2 px-2 py-1.5 text-xs cursor-pointer hover:bg-blue-50 border-b ${checked ? "bg-blue-50/50" : ""}`}
                data-testid={`zones-picker-item-${location_id}-${z.id}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(z.id)}
                />
                <span className="font-mono text-[10px] text-gray-500 w-14">{z.code || "—"}</span>
                <span className="flex-1">{z.name}</span>
              </label>
            );
          })}
        </div>
        <div className="flex items-center justify-between mt-2">
          <div className="text-[10px] text-gray-500">{selected.length} zona(s) seleccionada(s)</div>
          <div className="flex gap-1">
            <button
              onClick={onClose}
              className="px-2 py-1 border rounded text-[10px]"
              data-testid={`zones-picker-cancel-${location_id}`}
            >
              Cancelar
            </button>
            <button
              onClick={() => onSave(selected)}
              disabled={saving}
              className="px-2 py-1 rounded text-white text-[10px] font-bold flex items-center gap-1"
              style={{ background: "#0A3747" }}
              data-testid={`zones-picker-save-${location_id}`}
            >
              {saving ? <Loader2 size={10} className="animate-spin"/> : null}
              Guardar
            </button>
          </div>
        </div>
      </FloatingPanel>
    </div>
  );
}
